import { Card } from "@devhop/ui";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import type { AuditSummaryStats } from "@/modules/internal-audit/domain/repo/summary/get-stats";

type AuditSummaryPeriodComparisonProps = {
  stats: AuditSummaryStats;
};

export function AuditSummaryPeriodComparison({
  stats,
}: AuditSummaryPeriodComparisonProps) {
  const { periodComparison } = stats;
  const { current, previous } = periodComparison;

  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const totalChange = calculateChange(
    current.totalAudits,
    previous.totalAudits,
  );
  const passChange = calculateChange(current.passCount, previous.passCount);
  const failChange = calculateChange(current.failCount, previous.failCount);
  const needsImprovementChange = calculateChange(
    current.needsImprovementCount,
    previous.needsImprovementCount,
  );

  const renderChange = (change: number) => {
    if (change > 0) {
      return (
        <span className="flex items-center gap-1 text-green-600">
          <ArrowUp className="h-4 w-4" />
          {change}%
        </span>
      );
    }
    if (change < 0) {
      return (
        <span className="flex items-center gap-1 text-red-600">
          <ArrowDown className="h-4 w-4" />
          {Math.abs(change)}%
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-muted-foreground">
        <Minus className="h-4 w-4" />
        0%
      </span>
    );
  };

  return (
    <Card className="p-6" data-tourid="summary-period-comparison">
      <h2 className="mb-4 font-semibold text-lg">
        ການປຽບທຽບລະຫວ່າງລະດູການ
      </h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border bg-card p-4">
          <p className="text-muted-foreground text-sm">ຈຳນວນການກວດສອບ</p>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              <p className="text-2xl font-bold">{current.totalAudits}</p>
              <p className="text-muted-foreground text-xs">
                ກ່ອນໜ້ານີ້: {previous.totalAudits}
              </p>
            </div>
            {renderChange(totalChange)}
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <p className="text-muted-foreground text-sm">ຜ່ານ</p>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              <p className="text-2xl font-bold text-green-600">
                {current.passCount}
              </p>
              <p className="text-muted-foreground text-xs">
                ກ່ອນໜ້ານີ້: {previous.passCount}
              </p>
            </div>
            {renderChange(passChange)}
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <p className="text-muted-foreground text-sm">ບໍ່ຜ່ານ</p>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              <p className="text-2xl font-bold text-red-600">
                {current.failCount}
              </p>
              <p className="text-muted-foreground text-xs">
                ກ່ອນໜ້ານີ້: {previous.failCount}
              </p>
            </div>
            {renderChange(failChange)}
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <p className="text-muted-foreground text-sm">ຕ້ອງປັບປຸງ</p>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              <p className="text-2xl font-bold text-yellow-600">
                {current.needsImprovementCount}
              </p>
              <p className="text-muted-foreground text-xs">
                ກ່ອນໜ້ານີ້: {previous.needsImprovementCount}
              </p>
            </div>
            {renderChange(needsImprovementChange)}
          </div>
        </div>
      </div>
    </Card>
  );
}

